import { datadogRum } from '@datadog/browser-rum';

const MFT_SEGMENT = 'mft';

/**
 * The basename test used by history v4, reproduced so we can detect the case it
 * handles silently.
 *
 * Two properties matter and are deliberate: the comparison is case-insensitive, and
 * the character after the prefix must be a path, query or hash boundary — where an
 * empty string counts as a match, because `'/?#'.indexOf('')` is 0. That is what makes
 * a bare program root URL a match rather than a mismatch.
 */
export const hasProgramPathPrefix = (
  pathname: string,
  programPath: string
): boolean =>
  pathname.toLowerCase().indexOf(programPath.toLowerCase()) === 0 &&
  '/?#'.indexOf(pathname.charAt(programPath.length)) !== -1;

const firstTwoSegments = (pathname: string): string | null => {
  const segments = pathname.split('/');
  return segments.length >= 3 && segments[1] && segments[2]
    ? `/${segments[1]}/${segments[2]}`
    : null;
};

export type ProgramPathMismatch = {
  programPath: string;
  pathname: string;
  corrected: string | null;
};

const report = (mismatch: ProgramPathMismatch): void => {
  console.error(
    '[program-path] program_path is not a prefix of the current URL',
    mismatch
  );
  datadogRum.addError({
    error: new Error('program_path does not prefix the current URL'),
    params: mismatch,
  });
};

/**
 * Returns the program path to use for the router basename and for every
 * program-path-relative link helper.
 *
 * When program_path is not a prefix of the current URL, history v4 does not strip it —
 * it emits `basename + <un-stripped pathname>` for every href it builds, so the path
 * silently doubles and keeps growing with each navigation. history's own assertion for
 * this is compiled out of production builds, so it never surfaced. We would rather
 * trust the URL the user is demonstrably on and report the disagreement.
 *
 * This never collapses duplicate path segments: an org slug and a program slug that
 * happen to be identical is an ordinary program path and matches by prefix like any
 * other. The only thing compared is a prefix.
 */
export const resolveProgramPath = (
  programPath: string | undefined,
  pathname: string,
  onMismatch: (mismatch: ProgramPathMismatch) => void = report
): string => {
  // An empty program path is the custom-domain case: the SPA is mounted at the root.
  if (!programPath) return programPath ?? '';
  if (hasProgramPathPrefix(pathname, programPath)) return programPath;

  // MS Teams mobile URLs carry an `mft` segment that the basename does not, and the two
  // sides disagree about whether it is a prefix or a suffix. Observe, never act.
  const isTeamsPath = pathname.split('/').includes(MFT_SEGMENT);
  const corrected = isTeamsPath ? null : firstTwoSegments(pathname);

  try {
    onMismatch({ programPath, pathname, corrected });
  } catch {
    /* reporting must never be able to block app boot */
  }

  return corrected ?? programPath;
};
